import { LocalLink } from '@shared/util/link'
import Link from 'next/link'
import { useRouter } from 'next/router'
import {
    type ComponentProps,
    type FunctionComponent,
    type ReactNode,
    useEffect,
    useState,
} from 'react'

const isExternalHref = (href: string) =>
    href.startsWith('mailto:') ||
    href.startsWith('tel:') ||
    href.startsWith('http')

/**
 * A smart link that automatically preserves attribution parameters for internal navigation,
 * and renders a plain anchor for external URLs (mailto:, tel:, http/https).
 *
 * Attribution params preserved: UTM params, gclid, fbclid, pid, af_*, ttclid, ScCid, etc.
 */
export const AttributionLink: FunctionComponent<AttributionLinkProps> = ({
    href,
    children,
    ...props
}) => {
    if (isExternalHref(href)) {
        return (
            <a href={href} {...props}>
                {children}
            </a>
        )
    }

    return (
        <InternalAttributionLink href={href} {...props}>
            {children}
        </InternalAttributionLink>
    )
}

const InternalAttributionLink: FunctionComponent<AttributionLinkProps> = ({
    href,
    children,
    ...props
}) => {
    const router = useRouter()
    const [serializedHref, setSerializedHref] = useState(href)

    // Update the serialized href when router becomes ready or asPath changes
    // This ensures attribution params are preserved after hydration
    useEffect(() => {
        if (router.isReady) {
            const link = new LocalLink({ base: href })
            setSerializedHref(link.serialize(router))
        }
    }, [href, router.isReady, router.asPath])

    return (
        <Link href={serializedHref} {...props}>
            {children}
        </Link>
    )
}

interface AttributionLinkProps extends Omit<
    ComponentProps<typeof Link>,
    'href'
> {
    href: string
    children: ReactNode
}

export default AttributionLink
